import React, { useEffect, useState, useMemo } from 'react';
import { adminApi } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { BarChart3 } from 'lucide-react';
import { logger } from '../lib/logger';

function groupByCta(results) {
  const out = {};
  for (const r of results || []) {
    if (!out[r.cta]) out[r.cta] = { total: 0, variants: {} };
    out[r.cta].variants[r.variant] = (out[r.cta].variants[r.variant] || 0) + r.count;
    out[r.cta].total += r.count;
    if (!out[r.cta].last_at || r.last_at > out[r.cta].last_at) out[r.cta].last_at = r.last_at;
  }
  return out;
}

export default function AdminAbTests() {
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    adminApi.abResults()
      .then(({ results }) => setResults(results || []))
      .catch((e) => { logger.error('ab results failed', e); setError(e.message || 'Could not load A/B results.'); });
  }, []);

  const grouped = useMemo(() => groupByCta(results), [results]);

  if (error) {
    return (
      <Card className="border-destructive/30 bg-destructive/5">
        <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
      </Card>
    );
  }

  if (results === null) {
    return (
      <div className="space-y-2" data-testid="admin-ab-loading">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-ab-page">
      <div className="flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-[#004C54]" />
        <h2 className="font-heading text-lg font-bold">A/B Test Results</h2>
        <Badge variant="outline" className="border-foreground/15 text-foreground/60">{results.length} variants</Badge>
      </div>
      <p className="text-sm text-foreground/60">Tracked CTA clicks grouped by variant. Higher counts win.</p>

      {Object.keys(grouped).length === 0 && (
        <Card className="border-border"><CardContent className="p-6 text-center text-foreground/60" data-testid="admin-ab-empty">
          No clicks logged yet. Send some traffic to the marketing site to start collecting data.
        </CardContent></Card>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {Object.entries(grouped).map(([cta, info]) => {
          const variants = Object.entries(info.variants).sort((a, b) => b[1] - a[1]);
          const max = Math.max(1, ...variants.map(([, c]) => c));
          return (
            <Card key={cta} className="border-border" data-testid={`admin-ab-card-${cta}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-foreground/60 font-semibold">CTA</div>
                    <div className="font-heading text-base font-bold">{cta}</div>
                  </div>
                  <Badge variant="outline" className="border-foreground/15">{info.total} clicks</Badge>
                </div>
                <div className="space-y-2">
                  {variants.map(([variant, count]) => {
                    const pct = info.total ? Math.round((count / info.total) * 100) : 0;
                    return (
                      <div key={variant}>
                        <div className="flex items-center justify-between text-[12px] font-mono mb-1">
                          <span className="font-semibold">{variant}</span>
                          <span className="text-foreground/60">{count} · {pct}%</span>
                        </div>
                        <div className="h-1.5 bg-[#F0F4F4] rounded-full overflow-hidden">
                          <div className="h-full bg-[#004C54]" style={{ width: `${Math.max(2, Math.round((count / max) * 100))}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {info.last_at && <div className="text-[11px] text-foreground/45 mt-3">Last click: {new Date(info.last_at).toLocaleString()}</div>}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
